import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Alert,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Popover,
} from '@mui/material';
import { Sync, Search, Group, AdminPanelSettings, Person, Email as EmailIcon } from '@mui/icons-material';
import { usersApi } from '../services/api';

function SettingsPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [namePopover, setNamePopover] = useState({ anchor: null, user: null });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromEntra = async () => {
    try {
      setSyncing(true);
      setError(null);
      const result = await usersApi.syncAllFromEntra();
      await loadUsers();
      const parts = [];
      if (result.created > 0) parts.push(`${result.created} new users provisioned`);
      if (result.updated > 0) parts.push(`${result.updated} updated`);
      if (result.deactivated > 0) parts.push(`${result.deactivated} deactivated`);
      if (result.errors > 0) parts.push(`${result.errors} errors`);
      setSuccess(`Entra sync complete: ${parts.join(', ') || 'no changes'}. ${result.totalInGroups} users in groups.`);
      setTimeout(() => setSuccess(null), 8000);
    } catch (err) {
      setError('Failed to sync from Entra: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchFilter.trim()) return users;
    const q = searchFilter.toLowerCase();
    return users.filter(u =>
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.firstName || '').toLowerCase().includes(q) ||
      (u.lastName || '').toLowerCase().includes(q) ||
      (u.roles || []).some(r => r.toLowerCase().includes(q))
    );
  }, [users, searchFilter]);

  return (
    <div className="page-container">
      <Paper sx={{ p: 3, width: '100%', maxWidth: 1000, position: 'relative' }}>
        {/* Gradient scroll hint */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))', pointerEvents: 'none', zIndex: 1, borderRadius: '0 4px 4px 0' }} />
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          mb: 1,
        }}>
          <Typography variant="h5" fontWeight="bold">
            User Management
          </Typography>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <Sync />}
            onClick={handleSyncFromEntra}
            disabled={syncing}
            size="medium"
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            {syncing ? 'Syncing...' : 'Sync from Entra'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Users and roles are managed through Microsoft Entra security groups.
          Use the sync button to provision new users and update roles from Entra.
          Roles are also synced automatically on login and every 6 hours in cloud environments.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <TextField
          fullWidth
          placeholder="Search users by name, email, or role..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">Loading...</TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">
                      {searchFilter ? 'No users match your search.' : 'No users found. Click "Sync from Entra" to provision users from Entra groups.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        onClick={(e) => setNamePopover({ anchor: e.currentTarget, user })}
                        sx={{
                          cursor: { xs: 'pointer', sm: 'default' },
                          '&:hover': { textDecoration: { xs: 'underline', sm: 'none' } },
                        }}
                      >
                        {user.displayName || `${user.firstName} ${user.lastName}`.trim() || user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={user.roles?.[0] === 'Admin' ? <AdminPanelSettings /> : <Person />}
                        label={user.roles?.[0] || 'User'}
                        color={user.roles?.[0] === 'Admin' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile name popover — shows email on tap */}
        <Popover
          open={Boolean(namePopover.anchor)}
          anchorEl={namePopover.anchor}
          onClose={() => setNamePopover({ anchor: null, user: null })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {namePopover.user && (
            <Box sx={{ p: 2, maxWidth: 280 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {namePopover.user.displayName || namePopover.user.email}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {namePopover.user.email}
                </Typography>
              </Box>
            </Box>
          )}
        </Popover>
        {!loading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {filteredUsers.length} of {users.length} users shown
          </Typography>
        )}
      </Paper>

      {/* Entra Group → Role Mapping */}
      <Paper sx={{ p: 3, width: '100%', maxWidth: 1000 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Group sx={{ color: '#32578b' }} />
          <Typography variant="h6">Entra Group → Role Mapping</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Roles are determined by membership in these Microsoft Entra security groups.
          To change a user's role, add or remove them from the appropriate group in Entra.
          Nested group memberships are supported.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Entra Group</TableCell>
                <TableCell>App Role</TableCell>
                <TableCell>Permissions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminPanelSettings sx={{ color: '#32578b', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight="medium">QB-Archive-Admins</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip size="small" label="Admin" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    Full access: search, view, download, import data, manage users
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person sx={{ color: '#757575', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight="medium">QB-Archive-Users</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip size="small" label="User" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    Search, view, and download attachments only
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

export default SettingsPage;
